/**
 * /test-numbers — admin-managed WhatsApp test allowlist (Story 9.12).
 *
 * Surface:
 *   - Banner top: global toggle `test_allowlist_enabled` (admin-only switch).
 *     When ON, shows a yellow warning so the operator knows traffic from
 *     non-listed numbers is being silently dropped.
 *   - Paginated table of allowed numbers with active toggle + edit + delete.
 *   - Admin: CRUD. Operator/viewer: read-only (buttons disabled with title).
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Phone, Plus, Power } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/sonner';
import { ApiError, apiClient } from '@/lib/api';
import { formatDate } from '@/lib/format-date';
import { useAuthStore } from '@/stores/auth';
import { TestNumberForm, type TestNumberFormValue } from '@/components/test-numbers/TestNumberForm';

interface TestNumberDto {
  id: string;
  phone: string;
  label: string;
  active: boolean;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ListResponse {
  data: TestNumberDto[];
  page: number;
  pageSize: number;
  total: number;
}

interface SettingDto {
  key: string;
  value: unknown;
  valueType: string;
  category: string | null;
  description: string | null;
  isSecret: boolean;
  updatedAt: string;
}

const TOGGLE_KEY = 'test_allowlist_enabled';

function humanizeError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: { code?: string; message?: string } };
    const code = body?.error?.code;
    if (code === 'PHONE_TAKEN') return 'Já existe um número de teste com esse telefone.';
    if (code === 'TEST_NUMBER_NOT_FOUND') return 'Número de teste não encontrado.';
    if (code === 'VALIDATION_FAILED') return body.error?.message ?? 'Validação falhou.';
    if (body?.error?.message) return body.error.message;
    return `${fallback} (HTTP ${err.status})`;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

export function TestNumbersPage() {
  const role = useAuthStore((s) => s.user?.role);
  const isAdmin = role === 'admin';
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<TestNumberDto | null>(null);

  const listQuery = useQuery({
    queryKey: ['test-numbers'],
    queryFn: () => apiClient.get<ListResponse>('/admin/test-numbers?page=1&pageSize=100'),
  });

  const settingQuery = useQuery({
    queryKey: ['system-setting', TOGGLE_KEY],
    queryFn: () => apiClient.get<SettingDto>(`/admin/settings/${TOGGLE_KEY}`),
  });

  const allowlistEnabled = settingQuery.data?.value === true;

  const toggleMutation = useMutation({
    mutationFn: (next: boolean) =>
      apiClient.put<SettingDto>(`/admin/settings/${TOGGLE_KEY}`, { value: next }),
    onSuccess: (data) => {
      toast.success(data.value === true ? 'Allowlist ATIVADA.' : 'Allowlist desativada.');
      void queryClient.invalidateQueries({ queryKey: ['system-setting', TOGGLE_KEY] });
    },
    onError: (err) => toast.error(humanizeError(err, 'Falha ao alterar toggle.')),
  });

  const createMutation = useMutation({
    mutationFn: (body: TestNumberFormValue) =>
      apiClient.post<TestNumberDto>('/admin/test-numbers', body),
    onSuccess: () => {
      toast.success('Número cadastrado.');
      setCreateOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['test-numbers'] });
    },
    onError: (err) => toast.error(humanizeError(err, 'Falha ao cadastrar número.')),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: { label: string } }) =>
      apiClient.put<TestNumberDto>(`/admin/test-numbers/${id}`, body),
    onSuccess: () => {
      toast.success('Número atualizado.');
      setEditTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['test-numbers'] });
    },
    onError: (err) => toast.error(humanizeError(err, 'Falha ao atualizar número.')),
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete<TestNumberDto>(`/admin/test-numbers/${id}`),
    onSuccess: () => {
      toast.success('Número desativado.');
      void queryClient.invalidateQueries({ queryKey: ['test-numbers'] });
    },
    onError: (err) => toast.error(humanizeError(err, 'Falha ao desativar número.')),
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Números de Teste</h1>
          <p className="text-sm text-muted-foreground">
            Allowlist independente do Meta. Quando o toggle global está ativo, o agente só responde
            mensagens de números desta lista.
          </p>
        </div>
        <Button
          onClick={() => setCreateOpen(true)}
          disabled={!isAdmin}
          title={isAdmin ? undefined : 'Somente administradores podem cadastrar números.'}
        >
          <Plus className="mr-1" aria-hidden />
          Novo número
        </Button>
      </div>

      {/* Global toggle banner */}
      <Card
        className={
          allowlistEnabled ? 'border-amber-500/60 bg-amber-50/60 dark:bg-amber-950/20' : undefined
        }
      >
        <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
          <div className="space-y-1">
            <p className="text-sm font-medium">
              {allowlistEnabled
                ? '🟡 Allowlist ATIVA — apenas números desta lista recebem resposta.'
                : 'Allowlist DESATIVADA — o agente responde qualquer número que conseguir mandar mensagem.'}
            </p>
            {settingQuery.data?.description ? (
              <p className="text-xs text-muted-foreground">{settingQuery.data.description}</p>
            ) : null}
          </div>
          <Button
            variant={allowlistEnabled ? 'destructive' : 'default'}
            onClick={() => toggleMutation.mutate(!allowlistEnabled)}
            disabled={!isAdmin || toggleMutation.isPending || settingQuery.isLoading}
            title={isAdmin ? undefined : 'Somente administradores podem alterar o toggle.'}
          >
            {allowlistEnabled ? 'Desativar allowlist' : 'Ativar allowlist'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Números autorizados</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {listQuery.isLoading ? (
            <div className="p-6 text-sm text-muted-foreground">Carregando…</div>
          ) : listQuery.error ? (
            <div className="p-6 text-sm text-destructive">
              {humanizeError(listQuery.error, 'Falha ao carregar números.')}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" role="table">
                <thead className="border-b bg-muted/50 text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2">Telefone</th>
                    <th className="px-4 py-2">Rótulo</th>
                    <th className="px-4 py-2">Ativo</th>
                    <th className="px-4 py-2">Criado em</th>
                    <th className="px-4 py-2 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {listQuery.data && listQuery.data.data.length > 0 ? (
                    listQuery.data.data.map((row) => (
                      <tr key={row.id} className="border-b last:border-b-0">
                        <td className="px-4 py-2 font-mono text-xs">
                          <Phone className="mr-1 inline size-3" aria-hidden />
                          {row.phone}
                        </td>
                        <td className="max-w-[10rem] truncate px-4 py-2">{row.label}</td>
                        <td className="px-4 py-2">{row.active ? 'Sim' : 'Não'}</td>
                        <td className="px-4 py-2 text-muted-foreground">
                          {formatDate(row.createdAt)}
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex justify-end gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setEditTarget(row)}
                              aria-label={`Editar ${row.phone}`}
                              disabled={!isAdmin}
                              title={isAdmin ? undefined : 'Somente administradores.'}
                            >
                              <Pencil aria-hidden />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={!row.active || !isAdmin || deactivateMutation.isPending}
                              onClick={() => {
                                if (
                                  window.confirm(
                                    `Desativar o número ${row.phone} (${row.label})? Ele deixará de receber respostas.`,
                                  )
                                ) {
                                  deactivateMutation.mutate(row.id);
                                }
                              }}
                              aria-label={`Desativar ${row.phone}`}
                              title={isAdmin ? undefined : 'Somente administradores.'}
                            >
                              <Power aria-hidden />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                        Nenhum número cadastrado.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Novo número de teste</DialogTitle>
            <DialogDescription>
              Inclua os dígitos com DDI + DDD. O sistema normaliza para E.164 (ex.:{' '}
              <code>+5511999999999</code>).
            </DialogDescription>
          </DialogHeader>
          <TestNumberForm
            mode="create"
            submitting={createMutation.isPending}
            onCancel={() => setCreateOpen(false)}
            onSubmit={(next) => createMutation.mutate(next)}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={editTarget !== null} onOpenChange={(open) => !open && setEditTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar número de teste</DialogTitle>
          </DialogHeader>
          {editTarget ? (
            <TestNumberForm
              mode="edit"
              submitting={updateMutation.isPending}
              initial={{ phone: editTarget.phone, label: editTarget.label }}
              onCancel={() => setEditTarget(null)}
              onSubmit={(next) =>
                updateMutation.mutate({ id: editTarget.id, body: { label: next.label } })
              }
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default TestNumbersPage;
